import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";

import {
    REVIEWS_ADD_REPLY,
    REVIEWS_DELETE,
    REVIEWS_DELETE_REPLY,
    REVIEWS_LOAD,
    REVIEWS_READ,
    ReviewsAddReplyAction,
    ReviewsAddReplyNormalizedResponse,
    ReviewsDeleteAction,
    ReviewsDeleteNormalizedResponse,
    ReviewsDeleteReplyAction,
    ReviewsDeleteReplyNormalizedResponse,
    ReviewsLoadAction,
    ReviewsLoadNormalizedResponse,
    ReviewsReadAction,
    reviewsAddReplyApi,
    reviewsDeleteApi,
    reviewsDeleteReplyApi,
    reviewsLoadApi,
    reviewsReadApi,
} from "@/mk2/apps/bazaar/containers/Reviews/Reviews.actions";
import { REVIEW_REPLY_FORM } from "@/mk2/apps/bazaar/helpers";
import {
    XHRAction,
    XHRFormError,
    XHRGetError,
    XHRPostError,
    handleXHRGetErrorSaga,
    handleXHRPostErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { getLogger } from "@/mk2/logger";
import { HistoryAction } from "@/mk2/services/browserHistory";
import {
    ARE_YOU_SURE_YOU_WANT_TO_DELETE_REVIEW,
    ARE_YOU_SURE_YOU_WANT_TO_DELETE_REVIEW_REPLY,
} from "@/mk/autogenerated/translation";
import {
    bazaar_api_v3_reviews_delete_url,
    bazaar_api_v3_reviews_read_url,
    bazaar_api_v3_reviews_reply_add_url,
    bazaar_api_v3_reviews_reply_delete_url,
    bazaar_api_v3_reviews_url,
} from "@/mk/urls/functions";

const logger = getLogger("bazaar/Reviews.sagas");

interface LoadApiResponse {
    body: {
        entities: ReviewsLoadNormalizedResponse["entities"];
        result: ReviewsLoadNormalizedResponse["result"];
        hasNext: number;
        hasPrevious: number;
    };
}

function* load({
    xhr,
    username,
    role,
    after,
    history,
}: ReviewsLoadAction & XHRAction & HistoryAction) {
    yield put(reviewsLoadApi.request(username, role, after));

    try {
        const response: LoadApiResponse = yield call(() =>
            xhr.get(bazaar_api_v3_reviews_url(username, role), {
                after,
            }),
        );

        yield put(
            reviewsLoadApi.success(
                username,
                role,
                after,
                response.body.hasNext,
                response.body.hasPrevious,
                {
                    entities: response.body.entities,
                    result: response.body.result,
                },
            ),
        );
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, history, logger);
        yield put(reviewsLoadApi.failure(username, role, after, normalizeError(error)));
    }
}

interface AddReplyApiResponse {
    body: {
        entities: ReviewsAddReplyNormalizedResponse["entities"];
        result: ReviewsAddReplyNormalizedResponse["result"];
    };
}

function* addReply({
    reviewId,
    reply,
    xhr,
    history,
}: ReviewsAddReplyAction & XHRAction & HistoryAction) {
    yield put(reviewsAddReplyApi.request(reviewId, reply));

    const formName = REVIEW_REPLY_FORM(reviewId);

    try {
        const response: AddReplyApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_reviews_reply_add_url(reviewId), {
                ...reply,
            }),
        );

        yield handleXHRFormSuccessSaga(formName, history);
        yield put(
            reviewsAddReplyApi.success(reviewId, reply, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRFormErrorSaga(formName, error as XHRFormError, logger);
        yield put(reviewsAddReplyApi.failure(reviewId, reply, normalizeError(error)));
    }
}

interface DeleteReplyApiResponse {
    body: {
        entities: ReviewsDeleteReplyNormalizedResponse["entities"];
        result: ReviewsDeleteReplyNormalizedResponse["result"];
    };
}

function* deleteReply({
    reviewId,
    xhr,
    history,
}: ReviewsDeleteReplyAction & XHRAction & HistoryAction) {
    if (!window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_REVIEW_REPLY)) {
        return;
    }

    yield put(reviewsDeleteReplyApi.request(reviewId));

    try {
        const response: DeleteReplyApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_reviews_reply_delete_url(reviewId)),
        );

        yield put(
            reviewsDeleteReplyApi.success(reviewId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, history, logger);
        yield put(reviewsDeleteReplyApi.failure(reviewId, normalizeError(error)));
    }
}

interface DeleteApiResponse {
    body: {
        entities: ReviewsDeleteNormalizedResponse["entities"];
        result: ReviewsDeleteNormalizedResponse["result"];
    };
}

function* deleteReview({
    username,
    role,
    reviewId,
    xhr,
    history,
}: ReviewsDeleteAction & XHRAction & HistoryAction) {
    if (!window.confirm(ARE_YOU_SURE_YOU_WANT_TO_DELETE_REVIEW)) {
        return;
    }

    yield put(reviewsDeleteApi.request(username, role, reviewId));

    try {
        const response: DeleteApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_reviews_delete_url(reviewId)),
        );

        yield put(
            reviewsDeleteApi.success(username, role, reviewId, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, history, logger);
        yield put(reviewsDeleteApi.failure(username, role, reviewId, normalizeError(error)));
    }
}

interface ReadApiResponse {
    body: {
        read: number; // number of read reviews
    };
}

function* read({
    username,
    role,
    reviewsIds,
    xhr,
    history,
}: ReviewsReadAction & XHRAction & HistoryAction) {
    yield put(reviewsReadApi.request(username, role, reviewsIds));

    try {
        const response: ReadApiResponse = yield call(() =>
            xhr.post(bazaar_api_v3_reviews_read_url(username, role), {
                reviewsIds,
            }),
        );

        yield put(
            reviewsReadApi.success(username, role, reviewsIds, {
                result: null,
                entities: {},
            }),
        );
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, history, logger);
        yield put(reviewsReadApi.failure(username, role, reviewsIds, normalizeError(error)));
    }
}

export default function* root() {
    yield all([
        takeLatest(REVIEWS_LOAD, load),
        takeEvery(REVIEWS_ADD_REPLY, addReply),
        takeEvery(REVIEWS_DELETE_REPLY, deleteReply),
        takeEvery(REVIEWS_DELETE, deleteReview),
        takeEvery(REVIEWS_READ, read),
    ]);
}
